import React, { useContext, useMemo } from "react";
import { FieldPath, FieldValues } from "react-hook-form";
import { GroupBase } from "react-select";
import {
  ReactSelectSyncProps,
  ReactSelectAsyncProps,
} from "@pushpress/shared-components/build/components/inputs/ReactSelect";
import {
  Grid,
  Loader,
  ReactSelectController,
} from "@pushpress/shared-components";
import { ReactSelectControllerProps } from "@pushpress/shared-components/build/components/controllers/ReactSelectController";
import { sortByName } from "../../../utils/sortUtils";
import { AppContext } from "../../../context/app";
import {
  DiscountControllerOptionFragment,
  useGetDiscountsQuery,
} from "./DiscountSelect-generated.hooks";
import { AcceptedDiscountCategories } from "../../../graphql/graphql-generated.types";

function DiscountController<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  Option extends DiscountControllerOptionFragment,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: ReactSelectControllerProps<
    TFieldValues,
    TName,
    Option,
    IsMulti,
    Group
  > &
    (ReactSelectSyncProps | ReactSelectAsyncProps) & {
      category?: AcceptedDiscountCategories;
    },
) {
  const { category = "CALENDAR", ...other } = props;

  const app = useContext(AppContext);

  const { data, loading } = useGetDiscountsQuery({
    variables: {
      getDiscountInput: {
        clientUuid: app.client.uuid,
        code: undefined,
        active: true,
        category,
        discountUuid: null,
        slug: undefined,
      },
    },
  });

  const getOptionLabel = (option: DiscountControllerOptionFragment) =>
    option.name;

  const getOptionValue = (option: DiscountControllerOptionFragment) =>
    option.discountUuid;

  const options = useMemo(() => {
    if (!data) {
      return [];
    }
    return [...(data?.getDiscounts ?? [])].sort(sortByName);
  }, [data]);

  if (loading) {
    return (
      <Grid container>
        <Grid item xs={12} container justifyContent="center" paddingY={2}>
          <Loader />
        </Grid>
      </Grid>
    );
  }

  return (
    <ReactSelectController
      menuPortalTarget={document.body}
      {...other}
      placeholder="— Select —"
      options={options}
      getOptionLabel={getOptionLabel}
      getOptionValue={getOptionValue}
    />
  );
}

export default DiscountController;
